import { useCallback, useEffect, useRef } from 'react';
import type { DiagnosticParamPost } from '@/services/diagnosticsService';
import {
  runDiagnostic,
  type DiagnosticResultEvent,
  type DiagnosticType,
} from '@/services/diagnosticsSse';

/**
 * Runs diagnostics over SSE, keeping at most one stream open: a new run, a
 * `cancel()` or unmounting aborts the previous one.
 */
export function useDiagnosticRunner() {
  const controllerRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = null;
  }, []);

  const run = useCallback(
    <T>(
      deviceId: string,
      type: DiagnosticType,
      body: DiagnosticParamPost[]
    ): Promise<DiagnosticResultEvent<T>> => {
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      return runDiagnostic<T>({
        deviceId,
        type,
        body,
        signal: controller.signal,
      });
    },
    []
  );

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
      controllerRef.current = null;
    };
  }, []);

  return { run, cancel };
}

export default useDiagnosticRunner;
